<!--
  -  Copyright (c) Qwoach LLC 2023. All rights reserved.
  -  Unauthorized copying of this file, via any medium is strictly prohibited
  -  Proprietary and confidential
  -  Created by Artem Bogush <artem@qwoach.com> on 9/8/2023
  -->

<template>
<div class="flex-column">

	<div class="pager-table-root flex-container fill-height-container">

		<div class="files-table-content flex-container fill-height-container" :class="scrollingClass">
			<div class="fill-height-container" :class="contentClass">

				<slot v-if="hasData || forceShowEmptyList" name="items-container" :dataArray="dataArrayInner">
					<div :class="itemsContainerClass">

						<div v-for="(item, index) of dataArrayInner" :key="index + '_' + item.id"
							 :class="[itemClasses]">
							<slot
								name="item"
								:item="item"
								v-bind="$attrs"
								:index="index">

									<div class="qw-row show-on-hover-parent flex-nowrap"
										 :class="[{'clickable-item': isClickableItems}, itemPaddings, getItemClass(item)]"
										 @click="$emit('onItemClicked', item)">
										<div class="col px-0 overflow-hidden">
											<slot name="item-title" :item="item" :index="index">
												{{ item.title || item.name || "Item" }}
											</slot>
										</div>
										<slot name="item-postfix" :item="item" :index="index"></slot>
										<!--									<div class="layout-spacer"></div>-->
										<!--					:class="{'show-on-hover-item': !forceShowMenu(index)}"-->
										<div v-if="menuItems != null"
											 class="text-right px-2"
											 :style="{'min-width': menuMinWidth}"
										>
											<div class="display-inline-block">

												<OptionsMenu menu-icon="icon-ellipsis-vert"
															 :menu-items="getMenuItems(item)"
															 @onMenuVisibilityChanged="(isVisible) => onMenuVisibilityChanged(isVisible, index)"
															 @click="action => $emit('menuItemClicked', action, item)"/>
											</div>

										</div>
									</div>
									<hr v-if="showDivider" class="my-0"/>

							</slot>
						</div>


					</div>
				</slot>
				<slot name="empty-view" v-else-if="!isLoadingComputed && (hideEmptyViewFilterFunction() || (!hasFilter || !$slots['empty-view-filter'] && !$slots['empty-view-filter-title']))">
					<qw-empty>
						<template #title>
							<div>
								<slot name="empty-view-filter-title">
									No items to display
								</slot>
							</div>
						</template>
					</qw-empty>
				</slot>
				<slot name="empty-view-filter" v-else-if="!hideEmptyViewFilterFunction() && !isLoadingComputed && !hasData && hasFilter && ($slots['empty-view-filter'] || $slots['empty-view-filter-title'])">
					<qw-empty>
						<template #title>
							<div>
								<slot name="empty-view-filter-title">
									No items to display
								</slot>
							</div>
						</template>
						<template #sub-title>
							<div>
								<slot name="empty-view-filter-sub-title">
									Try adjusting your search criteria
								</slot>
							</div>
						</template>
					</qw-empty>
				</slot>


				<div v-if="isLoadingComputed" class="row mx-0 justify-content-center p-4"
					 :class="{'relative': hasData && !isLoadingMoreForceShow}"
					 style="min-height: 80px">
					<LoadingIndicator :is-loading="isLoadingComputed"
									  :class="{'no-loading-mask': hasData && !isLoadingMoreForceShow}"/>
				</div>

			</div>

			<InfiniteLoadingFixed
				ref="infiniteLoader"
				:distance="loadThresholdDistance"
				@infinite="onLoadMore"
				:identifier="infiniteLoadingStateResetter"
				:force-use-infinite-wrapper="scrollContainerSelectorComputed"/>



		</div>
	</div>
</div>
</template>

<script>
import BaseComponent from "@/core/components/BaseComponent.vue";
import InfiniteLoading from 'vue-infinite-loading';
import LoadingIndicator from "@/core/components/LoadingIndicator.vue";
import OptionsMenu from "@/core/components/Menu/OptionsMenu.vue";
import InfiniteLoadingFixed from "@/core/components/Data/InfiniteLoadingFixed.vue";
import BaseIdObject from "@shared/BaseIdObject.js";
import {FirestorePager} from "@qwoach/firebase-data-manager";

export default {
	name: "PagerListView",
	extends: BaseComponent,
	components: {InfiniteLoadingFixed, OptionsMenu, LoadingIndicator, InfiniteLoading},

	inject: ['registerPagerListView'],

	props: {
		pagerProvider: [FirestorePager, Function],
		hideEmptyViewFilter: [Boolean, Function],
		postFilter: {Function, default: () => data => data},
		scrollContainerSelector: {String, default: '.el-main'},
		isClickableItems: {Boolean, default: true},
		itemPaddings: {String, default: "px-2 py-3"},
		itemClasses: String,
		menuMinWidth: String,
		menuItems: [Array, Function],
		showDivider: {Boolean, default: true},
		itemsContainerClass: String,
		forceShowEmptyList: Boolean,
		scrollingClass: String,
		itemClass: [String, Function],
		contentClass: String,
	},

	data() {
		return {
			loadThresholdDistance: 100,
			isLoadingMore: false,
			dataPager: null,
			dataArrayInner: [],
			infiniteLoadingStateResetter: 0,
			menuItemsCache: {},
			menuVisibleIndex: -1,
			hasMore: true,
			hasFilter: false,
			isForceLoadTriggered: false,
			isLoadingMoreForceShow: false,
		}
	},

	created(){
		if(this.registerPagerListView){
			this.registerPagerListView(this)
		}
		this.loadData()
	},



	methods: {


		async loadData() {
			this.resetInfiniteLoader()
			this.$nextTick(() => this.forceLoadFirstTime())
			// await this.onLoadMore()
		},

		async loadDataAll(){
			if(this.hasMore){
				await this.onLoadMore(null, {forceLoadAll: true})
			}
		},

		resetInfiniteLoader() {
			this.infiniteLoadingStateResetter++
		},

		getItemClass(item){
			if(this.itemClass){
				return typeof this.itemClass === "function" ? this.itemClass(item) : this.itemClass
			}
			return ""
		},

		forceLoadFirstTime() {
			// let dst = this.$refs.infiniteLoader.distance
			// this.$refs.infiniteLoader.distance = Number.MAX_VALUE
			// this.$refs.infiniteLoader.attemptLoad()
			// this.$refs.infiniteLoader.distance = dst
			if(this.isForceLoadTriggered){
				return
			}
			this.isForceLoadTriggered = true

			this.hasMore = true

			// console.trace("forceLoadFirstTime: this.$refs.infiniteLoader = ", this.$refs.infiniteLoader)
			let dst = this.loadThresholdDistance
			this.loadThresholdDistance = Number.MAX_VALUE
			this.dataPager = null
			this.dataArrayInner = []
			this.$nextTick(() => {
				console.log("PagerListView.forceLoadFirstTime: this.$refs.infiniteLoader = ", this.$refs.infiniteLoader)
				if (this.$refs.infiniteLoader) {
					this.$refs.infiniteLoader.attemptLoad()
				} else {
					this.onLoadMore()
				}
				this.loadThresholdDistance = dst
				this.isForceLoadTriggered = false
			})

		},

		async onLoadMore(state, options = {}) {
			console.log("PagerListView.onLoadMore(): ", state, this)
			if (this.isLoadingMore) {
				return
			}
			if (this.analyticsProps) {
				let _ = this.$analyticManager.trackEvent("Data portion loaded", this.analyticsPropsMethod())
			}
			this._showLoadingIndicatorInternal(true)

			let {hasMore, hasFilter} = await this._onLoadMoreInternal(options)
			this.onLoadingCompleted(hasMore, hasFilter)
			//quack
			if (state && typeof state.loaded === "function") {
				if (hasMore) {
					state.loaded()
				} else {
					state.complete()
				}
			}
		},

		forceShowLoadingIndicator(show = true){
			this.isLoadingMoreForceShow = show
		},

		_showLoadingIndicatorInternal(show){
			//don't call this method from outside since if this.isLoadingMore is true, it will prevent from the load attempt.
			console.log("PagerListView.showLoadingIndicator: show = ", show, ", this.isLoadingMore = ", this.isLoadingMore)
			this.isLoadingMore = show
			if(!show){
				this.isLoadingMoreForceShow = false
			}
			this.notifyLoadingStateChanged()
		},

		onLoadingCompleted(hasMore, hasFilter) {
			this.hasMore = hasMore
			this.hasFilter = hasFilter
			this._showLoadingIndicatorInternal(false)

		},

		getObjectHashForMenu(item){
			return item.id ? item.id : item
		},


		menuInvalidateCache(item){
			let objectHash = this.getObjectHashForMenu(item)
			// this.$set(this.menuItemsCache, objectHash, null)
			this.menuItemsCache[objectHash] = null
		},

		getMenuItems(item) {
			let menuItems
			if (typeof this.menuItems === "function") {
				let objectHash = this.getObjectHashForMenu(item)
				// console.log("PagerListLayout.getMenuItems: objectHash = ", objectHash)
				if (!this.menuItemsCache[objectHash]) {
					menuItems = this.menuItems(item).filter(item => !!item) //filter out any null/empty menu items
					// this.$set(this.menuItemsCache, objectHash, menuItems)
					this.menuItemsCache[objectHash] = menuItems
				}
				menuItems = this.menuItemsCache[objectHash]
				// console.log("PagerListLayout.getMenuItems: this.menuItemsCache = ", this.menuItemsCache)
			} else {
				menuItems = this.menuItems
			}
			// console.log("PagerListLayout.getMenuItems: = ", menuItems)

			return menuItems
		},


		notifyLoadingStateChanged(){
			this.$emit("onLoadingStateChanged", this.isLoadingMore, this.hasMore, this.dataArrayInner)
			if(!this.isLoadingMore){
				this.$emit("dataLoaded", this.dataArrayInner, this.hasMore)
			}
		},

		async _onLoadMoreInternal(options = {}) {
			console.log("PagerListView.onLoadMore", this)
			let hasMore = false
			let hasFilter = false
			try {
				let dataPager = await this.getDataPager()
				let newData
				if(options.forceLoadAll){
					console.log("PagerListView.onLoadMore: forceLoadAll = true, loading all data")
					newData = await dataPager.loadAll()
					this.dataArrayInner = [] //reset dataArrayInner to avoid duplicates
				} else {
					console.log("PagerListView.onLoadMore: loading next data portion")
					newData = await dataPager.next()
				}

				hasMore = dataPager.hasMore
				//TODO finish logic to determine if the filters are the same
				// if(this.defaultFilter){
				// 	hasFilter = !dataPager.isFilterSame(this.defaultFilter)
				// } else {
				hasFilter = (((dataPager.dataFilter || {}).filterOptions || {}).filterOptions || []).length > 0
				// }
				console.log("PagerListView.onLoadMore: hasFilter = ", hasFilter, ", dataPager = ", dataPager)
				// console.log("BasePagerView.onLoadMore: dataPager.hasFilterOptions() = ", dataPager.hasFilterOptions())
				await this.onDataChangedInternal(newData, false)
				return {hasMore, hasFilter}
			} catch (error) {
				this.hasMore = false
				this.handleError(error)
			}
			return {hasMore: false, hasFilter: false}
		},

		async onDataChangedInternal(newData, pagerKey, isInitialLoad) {
			console.log("PagerListView.onDataChangedInternal = ", newData)
			//mergePagerData must go before onDataChanged to make sure proper notification
			await this.mergePagerData(newData, pagerKey)
			await this.onDataChanged(newData, pagerKey, isInitialLoad)
		},

		async mergePagerData(newData, pagerKey) {
			let dataPager = await this.getDataPager(pagerKey)
			newData = await this.postFilter(newData)
			dataPager.mergeData(this.dataArrayInner, newData)
		},

		async onDataChanged(newData, pagerKey, isInitialLoad) {
			console.log("PagerListView.onDataChanged", this)
			// return await this.emitPromise('onDataLoaded', newData, pagerKey, isInitialLoad)
		},


		async getDataPager() {
			console.log("PagerListView.dataPagerProvider")
			if(!this.dataPager) {
				this.dataPager = await this.pagerProviderInternal()
			}

			return this.dataPager
		},

		async pagerProviderInternal(){
			if(typeof this.pagerProvider === "function"){
				return this.pagerProvider()
			}
			return this.pagerProvider// ? this.pagerProvider() : new PagerLocal(this.dataArray)
		},

		analyticsPropsInternal() {
			return {
				category: this.analyticsProps.category || "Pager table"
			}
		},

		hideEmptyViewFilterFunction(){
			let res = this.hideEmptyViewFilter
			let dataPager = this.dataPager
			// if(this.defaultFilter && dataPager.dataFilter === this.defaultFilter){
			// 	return true
			// }
			if(typeof this.hideEmptyViewFilter === "function"){
				res = this.hideEmptyViewFilter(dataPager)
			}
			console.log("PagerListView.hideEmptyViewFilterFunction: dataPager = ", dataPager, ", res = ", res)

			return res
		},

		onMenuVisibilityChanged(isVisible, index) {
			this.menuVisibleIndex = isVisible ? index : -1
		},

		notifyElementDeleted(elements) {
			if(!Array.isArray(elements)){
				elements = [elements]
			}
			for(let element of elements) {
				let predicate = this.getElementSearchPredicate(element)
				let index = this.dataArrayInner.findIndex(predicate)
				if(index >= 0){
					this.dataArrayInner.splice(index, 1)
				}
			}
		},
		notifyElementUpdated(elements) {
			if(!Array.isArray(elements)){
				elements = [elements]
			}
			for(let element of elements){
				this.menuInvalidateCache(element)
				let predicate = this.getElementSearchPredicate(element)
				let index = this.dataArrayInner.findIndex(predicate)
				if(index >= 0){
					this.dataArrayInner.splice(index, 1, element)
				}
				console.log("PagerListView.notifyElementUpdated: index = ", index, ", element = ", element, ", this.dataArrayInner = ", this.dataArrayInner, ", predicate = ", predicate)
			}

		},
		notifyElementAdded(elements) {
			if(!Array.isArray(elements)){
				elements = [elements]
			}
			elements = elements.filter(elem => !this.dataArrayInner.find(this.getElementSearchPredicate(elem)))
			this.dataArrayInner.unshift(...elements)
		},

		getElementSearchPredicate(element) {
			let isDbObject = element instanceof BaseIdObject
			return isDbObject ? t => t.id === element.id : t => t === element
		},

		getData(){
			return this.dataArrayInner
		}


	},

	computed: {
		hasData() {
			return this.dataArrayInner.length > 0
		},

		scrollContainerSelectorComputed(){
			return this.scrollingClass ? `.${this.scrollingClass}` : this.scrollContainerSelector
		},

		isLoadingComputed(){
			return this.isLoadingMore || this.isLoadingMoreForceShow
		}
	},
}
</script>

<style lang="scss">

</style>
